import React from 'react';
import { withFormik } from 'formik';
import * as Yup from 'yup';

import styles from './MemberForm.module.scss';

import {
  Button,
  Input,
  Label,
  FormGroup,
  FormFeedback,
  Modal,
  ModalBody,
  ModalHeader,
} from 'reactstrap';
import Cohort from './Cohort';
import { noop } from 'lodash';

const EditMemberForm = ({
  member,
  values,
  errors,
  handleChange,
  setFieldValue,
  handleBlur,
  handleReset,
  touched,
  isSubmitting,
  handleSubmit,
  cohorts,
  onCancel = noop,
  onRemove = noop,
}) => {
  const formikAttributes = (fieldName) => ({
    id: fieldName,
    name: fieldName,
    value: values[fieldName],
    onChange: handleChange,
    onBlur: handleBlur,
    invalid: errors[fieldName] && touched[fieldName],
  });

  const formikFeedback = (fieldName) => {
    return errors[fieldName] && touched[fieldName] ? (
      <FormFeedback>{errors[fieldName]}</FormFeedback>
    ) : null;
  };

  const handleCancel = () => {
    handleReset();
    onCancel();
  };

  const handleRemove = () => {
    onRemove(member);
  };

  return (
    <Modal isOpen={true} size="lg">
      <ModalHeader>Edit member</ModalHeader>
      <ModalBody>
        <form
          onSubmit={handleSubmit}
          className={`form ${styles['member-modal']}`}
        >
          <div className={styles['form-body']}>
            <div className={styles['member-name']}>
              <Label for="name">Name</Label>
              <FormGroup>
                <Input type="text" {...formikAttributes('name')} />
                {formikFeedback('name')}
              </FormGroup>
            </div>
            <div className={styles['member-email']}>
              <Label for="email">Email</Label>
              <FormGroup>{member?.user_group_member?.email}</FormGroup>
            </div>
            <div className={styles['member-title']}>
              <Label for="title">Title</Label>
              <FormGroup>
                <Input type="text" {...formikAttributes('title')} />
                {formikFeedback('title')}
              </FormGroup>
            </div>

            <div className={styles['member-cohort']}>
              <Label for="cohort">Cohort</Label>
              <FormGroup>
                <Cohort
                  placeholder="(None)"
                  list={cohorts}
                  includeStandardRoles={true}
                  onChange={(value) => setFieldValue('cohort', value)}
                  value={values['cohort']}
                />
              </FormGroup>
            </div>
            <div className={styles['member-actions']}>
              <Button color="primary" disabled={isSubmitting} type="submit">
                Update
              </Button>
              <Button
                color="secondary"
                disabled={isSubmitting}
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                color="danger"
                disabled={isSubmitting}
                onClick={handleRemove}
              >
                Remove
              </Button>
            </div>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

const FormikEditMemberForm = withFormik({
  mapPropsToValues: ({ member = {} }) => {
    const { cohort = null, user_group_member: userGroupMember = {} } = member;
    const { name = null, title = null, email = null } = userGroupMember;

    return {
      name: name ?? '',
      title: title ?? '',
      cohort: cohort ?? '',
      email: email ?? '',
    };
  },
  validationSchema: Yup.object().shape({
    email: Yup.string()
      .trim()
      .required("Our robots can't deliver assessments without an email address")
      .email("Hmm, that doesn't look like a valid email address yet")
      .max(64),
    name: Yup.string()
      .trim()
      .required(
        "I've been through the desert on a horse with no name, but all your members need one"
      )
      .max(
        128,
        "That's a long name you got there. Unfortunately we cut you off at 128 characters."
      ),
  }),
  handleSubmit: async (values, bag) => {
    const {
      setSubmitting,
      props: { onSubmit = () => {} },
    } = bag;
    // do nothing
    setSubmitting(true);
    try {
      await onSubmit(values);
    } finally {
      setSubmitting(false);
    }
  },
  displayName: 'EditMember',
})(EditMemberForm);

export default FormikEditMemberForm;
